import { Reducer } from 'redux';
import { update } from '../helpers/immutabilityHelper'
import * as Dtos from '../dtos/Tmd.dtos';

import {
    ACTION_SITELOCATIONS_LOAD,
    ACTION_SITELOCATIONS_LOAD_BY_ID,
    ACTION_SITELOCATIONS_LOAD_FAILURE,
    ACTION_SITELOCATIONS_LOAD_SUCCESS,

    ACTION_SITELOCATIONS_SAVE,
    ACTION_SITELOCATIONS_SAVE_SUCCESS,
    ACTION_SITELOCATIONS_SAVE_FAILURE,

    ACTION_SITELOCATIONS_CREATE,
    ACTION_SITELOCATIONS_CREATE_SUCCESS,
    ACTION_SITELOCATIONS_CREATE_FAILURE,

    ACTION_SITELOCATIONS_VALIDATE,
    ACTION_SITELOCATIONS_VALIDATE_SUCCESS,
    ACTION_SITELOCATIONS_VALIDATE_FAILURE,

    ACTION_SITELOCATIONS_CLEAR,
    ACTION_SITELOCATIONS_FILTER,

    SiteLocationAction
} from '../actions/siteLocations';
import { RequestState } from "../enumerations/RequestState";
import { IRequestState, IFormFilter } from "./index";
import { updateFailureIndexToId } from "../helpers/formErrorHelper";


export interface ISiteLocationState {
    formData: Dtos.SiteLocation | Dtos.SiteLocation[];
    formState: Dtos.FormState;
    formProperties: Dtos.FormProperty[];
    validationFailures: Dtos.ResponseError[];
    lookups: Dtos.Lookup[];
    loadState: IRequestState;
    saveState: IRequestState;
    updateState: IRequestState;
    createState: IRequestState;
    validateState: IRequestState;
    formFilter: IFormFilter;
    formFilteredData: Dtos.SiteLocation[];
}

const initialState: ISiteLocationState = {
    formData: undefined,
    formState: undefined,
    formProperties: undefined,
    validationFailures: undefined,
    lookups: undefined,
    loadState: {
        status: RequestState.None
    } as IRequestState,
    saveState: {
        status: RequestState.None
    } as IRequestState,
    updateState: {
        status: RequestState.None
    } as IRequestState,
    createState: {
        status: RequestState.None
    } as IRequestState,
    validateState: {
        status: RequestState.None
    } as IRequestState,
    formFilter: {
        search: "",
        showInActive: false
    },
    formFilteredData: []
};


function loading(state: ISiteLocationState, _isLoading: boolean): ISiteLocationState {
    const newState: ISiteLocationState = update(state, {
        loadState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function loadSuccess(state: ISiteLocationState,
    formData: Dtos.SiteLocation | Dtos.SiteLocation[],
    formState: Dtos.FormState,
    formProperties: Dtos.FormProperty[],
    lookups: Dtos.Lookup[],
    responseStatus: Dtos.ResponseStatus
): ISiteLocationState {
    const newState: ISiteLocationState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        formProperties: {
            $set: formProperties
        },
        lookups: {
            $set: lookups
        },
        loadState: {
            $set: {
                status: RequestState.Success,
                errorCode: responseStatus ? responseStatus.errorCode : undefined,
                errorMessage: responseStatus ? responseStatus.message : undefined,
                errors: responseStatus ? responseStatus.errors : undefined,
                meta: responseStatus ? responseStatus.meta : undefined
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, formData) : undefined
        }
    });

    return filter(newState, state.formFilter);
}

function filter(state: ISiteLocationState, formFilter: IFormFilter): ISiteLocationState {

    const sitelocations: Dtos.SiteLocation[] | undefined =
        state.formData instanceof Array ?
            state.formData :
            undefined;

    const formFilteredData = sitelocations ? sitelocations.filter(p => (p.active == true || formFilter.showInActive) &&
        (formFilter.search == "" || p.value.toLowerCase().indexOf(formFilter.search.toLowerCase()) > -1)) : undefined;

    const newState: ISiteLocationState = update(state, {
        formFilter: {
            $set: formFilter
        },
        formFilteredData: {
            $set: formFilteredData
        }
    });

    return newState;
}

function loadFailure(state: ISiteLocationState, responseStatus: Dtos.ResponseStatus): ISiteLocationState {
    const newState: ISiteLocationState = update(state, {
        loadState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus?.errorCode,
                errorMessage: responseStatus?.message,
                errors: responseStatus?.errors
            } as IRequestState
        }
    });

    return newState;
}

function clear(state: ISiteLocationState, propertiesToClear?: string[]): ISiteLocationState {

    if (!propertiesToClear) {
        let formFilter = state.formFilter;
        const newState: ISiteLocationState = update(initialState, {
            formFilter: {
                $set: formFilter
            }
        });
        return newState;
    }

    let stateModifier: any = {};

    propertiesToClear.forEach(propertyToClear => {
        stateModifier[propertyToClear] = { $set: initialState[propertyToClear] }
    });

    const newState = update(state, stateModifier);

    return newState;
}

function saving(state: ISiteLocationState, _isSaving: boolean): ISiteLocationState {
    const newState: ISiteLocationState = update(state, {
        saveState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function saveSuccess(
    state: ISiteLocationState,
    formData: Dtos.SiteLocation | Dtos.SiteLocation[],
    formState: Dtos.FormState,
    responseStatus: Dtos.ResponseStatus
): ISiteLocationState {
    const newState: ISiteLocationState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        saveState: {
            $set: {
                status: RequestState.Success,
                errorCode: responseStatus ? responseStatus.errorCode : undefined,
                errorMessage: responseStatus ? responseStatus.message : undefined,
                errors: responseStatus ? responseStatus.errors : undefined,
                meta: responseStatus ? responseStatus.meta : undefined
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, formData) : undefined
        }
    });

    return newState;
}

function saveFailure(state: ISiteLocationState, responseStatus: Dtos.ResponseStatus): ISiteLocationState {
    const newState: ISiteLocationState = update(state, {
        saveState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus?.errorCode,
                errorMessage: responseStatus?.message,
                errors: responseStatus?.errors,
                meta: responseStatus.meta
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, state.formData) : undefined
        }
    });

    return newState;
}

function creating(state: ISiteLocationState, _isCreating: boolean): ISiteLocationState {
    const newState: ISiteLocationState = update(state, {
        createState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function createSuccess(
    state: ISiteLocationState,
    formData: Dtos.SiteLocation,
    formState: Dtos.FormState,
    formProperties: Dtos.FormProperty[],
    lookups: Dtos.Lookup[]
): ISiteLocationState {
    const newState: ISiteLocationState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        formProperties: {
            $set: formProperties
        },
        lookups: {
            $set: lookups
        },
        createState: {
            $set: {
                status: RequestState.Success
            } as IRequestState
        }
    });

    return newState;
}

function createFailure(state: ISiteLocationState, responseStatus: Dtos.ResponseStatus): ISiteLocationState {
    const newState: ISiteLocationState = update(state, {
        createState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus?.errorCode,
                errorMessage: responseStatus?.message,
                errors: responseStatus?.errors
            } as IRequestState
        }
    });

    return newState;
}

function validating(state: ISiteLocationState, _isValidating: boolean): ISiteLocationState {
    const newState: ISiteLocationState = update(state, {
        validatingState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function validateSuccess(
    state: ISiteLocationState,
    formData: Dtos.SiteLocation,
    formState: Dtos.FormState,
    responseStatus: Dtos.ResponseStatus
): ISiteLocationState {
    const newState: ISiteLocationState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        validateState: {
            $set: {
                status: RequestState.Success,
                errorCode: responseStatus ? responseStatus.errorCode : undefined,
                errorMessage: responseStatus ? responseStatus.message : undefined,
                errors: responseStatus ? responseStatus.errors : undefined,
                meta: responseStatus ? responseStatus.meta : undefined
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, formData) : undefined
        }
    });

    return newState;
}

function validateFailure(state: ISiteLocationState, responseStatus: Dtos.ResponseStatus): ISiteLocationState {
    const newState: ISiteLocationState = update(state, {
        validateState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus?.errorCode,
                errorMessage: responseStatus?.message,
                errors: responseStatus?.errors,
                meta: responseStatus.meta
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, state.formData) : undefined
        }
    });

    return newState;
}

const SiteLocationReducer: Reducer<ISiteLocationState> = (state: ISiteLocationState = initialState, action: SiteLocationAction) => {
    switch (action.type) {
        case ACTION_SITELOCATIONS_LOAD:
        case ACTION_SITELOCATIONS_LOAD_BY_ID:
            return loading(state, true);
        case ACTION_SITELOCATIONS_LOAD_SUCCESS:
            return loadSuccess(state, action.formData, action.formState, action.formProperties, action.lookups, action.responseStatus);
        case ACTION_SITELOCATIONS_LOAD_FAILURE:
            return loadFailure(state, action.responseStatus);
        case ACTION_SITELOCATIONS_CLEAR:
            return clear(state, action.propertiesToClear);
        case ACTION_SITELOCATIONS_SAVE:
            return saving(state, true);
        case ACTION_SITELOCATIONS_SAVE_SUCCESS:
            return saveSuccess(state, action.formData, action.formState, action.responseStatus);
        case ACTION_SITELOCATIONS_SAVE_FAILURE:
            return saveFailure(state, action.responseStatus);
        case ACTION_SITELOCATIONS_VALIDATE:
            return validating(state, true);
        case ACTION_SITELOCATIONS_VALIDATE_SUCCESS:
            return validateSuccess(state, action.formData, action.formState, action.responseStatus);
        case ACTION_SITELOCATIONS_VALIDATE_FAILURE:
            return validateFailure(state, action.responseStatus);
        case ACTION_SITELOCATIONS_CREATE:
            return creating(state, true);
        case ACTION_SITELOCATIONS_CREATE_SUCCESS:
            return createSuccess(state, action.formData, action.formState, action.formProperties, action.lookups);
        case ACTION_SITELOCATIONS_CREATE_FAILURE:
            return createFailure(state, action.responseStatus);
        case ACTION_SITELOCATIONS_FILTER:
            return filter(state, action.formFilter);

    }

    return state;
}

export default SiteLocationReducer;